import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Plus, CalendarDays, MapPin, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Matches() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState("all"); // all | internal | official
  const [form, setForm] = useState({
    title: "",
    match_date: "",
    location: "",
    notes: "",
    match_type: "internal",
    opponent_name: "",
    home: true,
    team_a_name: "",
    team_b_name: "",
  });
  const [loading, setLoading] = useState(true);
  const isCoach = user?.role === "coach" || user?.role === "admin";

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/matches");
    setMatches(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const createMatch = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        match_date: new Date(form.match_date).toISOString(),
      };
      await api.post("/matches", payload);
      toast.success("Match programmé");
      setShowCreate(false);
      setForm({
        title: "",
        match_date: "",
        location: "",
        notes: "",
        match_type: "internal",
        opponent_name: "",
        home: true,
        team_a_name: "",
        team_b_name: "",
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteMatch = async (id) => {
    if (!confirm("Supprimer ce match ?")) return;
    try {
      await api.delete(`/matches/${id}`);
      toast.success("Match supprimé");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const upcoming = matches.filter((m) => m.status !== "finished" && (filter === "all" || m.match_type === filter));
  const finished = matches.filter((m) => m.status === "finished" && (filter === "all" || m.match_type === filter));

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
            Calendrier
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
            Matchs
          </h1>
        </div>
        {isCoach && (
          <button
            data-testid="create-match-button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg text-sm"
          >
            <Plus className="w-4 h-4" /> Nouveau match
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 p-1 rounded-xl bg-slate-900 border border-white/10 w-fit">
        {[
          { key: "all", label: "Tous" },
          { key: "internal", label: "Internes (B vs R)" },
          { key: "official", label: "Officiels" },
          { key: "gala", label: "Gala" },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors ${
              filter === f.key
                ? "bg-[#E5B85C] text-[#0B0F17]"
                : "text-slate-300 hover:bg-slate-800"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>
      ) : (
        <>
          <section>
            <h2 className="font-display font-bold text-xl uppercase mb-4 text-slate-300">
              À venir · {upcoming.length}
            </h2>
            <div className="space-y-3">
              {upcoming.length === 0 && (
                <div className="p-6 rounded-xl border border-dashed border-white/10 text-center text-slate-500">
                  Aucun match à venir
                </div>
              )}
              {upcoming.map((m) => (
                <MatchRow key={m.id} m={m} isCoach={isCoach} onDelete={deleteMatch} />
              ))}
            </div>
          </section>

          {finished.length > 0 && (
            <section>
              <h2 className="font-display font-bold text-xl uppercase mb-4 text-slate-300">
                Terminés · {finished.length}
              </h2>
              <div className="space-y-3">
                {finished.map((m) => (
                  <MatchRow key={m.id} m={m} isCoach={isCoach} onDelete={deleteMatch} finished />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-[#0B0F17]/90 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-bold text-2xl uppercase">Nouveau match</h3>
              <button
                data-testid="close-create-match"
                onClick={() => setShowCreate(false)}
                className="p-2 rounded-lg hover:bg-slate-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={createMatch} className="space-y-4">
              <div>
                <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
                  Type de match
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: "internal", label: "Interne (B vs R)" },
                    { v: "official", label: "Officiel" },
                    { v: "gala", label: "Gala" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      data-testid={`match-type-${o.v}`}
                      onClick={() => setForm({ ...form, match_type: o.v })}
                      className={`py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide ${
                        form.match_type === o.v
                          ? "bg-[#E5B85C] text-[#0B0F17]"
                          : "bg-slate-800 text-slate-300"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <Field label="Titre (optionnel)">
                <input
                  data-testid="match-title-input"
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                  placeholder="Match amical, tournoi..."
                />
              </Field>
              {form.match_type === "gala" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Nom équipe A">
                    <input
                      data-testid="match-team-a-input"
                      required
                      type="text"
                      value={form.team_a_name}
                      onChange={(e) => setForm({ ...form, team_a_name: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                      placeholder="Ex : Les Anciens"
                    />
                  </Field>
                  <Field label="Nom équipe B">
                    <input
                      data-testid="match-team-b-input"
                      required
                      type="text"
                      value={form.team_b_name}
                      onChange={(e) => setForm({ ...form, team_b_name: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                      placeholder="Ex : Les Jeunes"
                    />
                  </Field>
                </div>
              )}
              {form.match_type === "official" && (
                <>
                  <Field label="Nom de l'équipe adverse">
                    <input
                      data-testid="match-opponent-input"
                      required
                      type="text"
                      value={form.opponent_name}
                      onChange={(e) => setForm({ ...form, opponent_name: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                      placeholder="AS Fantom"
                    />
                  </Field>
                  <Field label="Type de terrain">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: true, label: "Domicile" },
                        { v: false, label: "Extérieur" },
                      ].map((o) => (
                        <button
                          key={String(o.v)}
                          type="button"
                          data-testid={`match-home-${o.v}`}
                          onClick={() => setForm({ ...form, home: o.v })}
                          className={`py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide ${
                            form.home === o.v
                              ? "bg-[#E5B85C] text-[#0B0F17]"
                              : "bg-slate-800 text-slate-300"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </Field>
                </>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Date & heure">
                  <input
                    data-testid="match-date-input"
                    type="datetime-local"
                    required
                    value={form.match_date}
                    onChange={(e) => setForm({ ...form, match_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                  />
                </Field>
                <Field label="Lieu">
                  <input
                    data-testid="match-location-input"
                    type="text"
                    required
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                    placeholder="Stade Municipal"
                  />
                </Field>
              </div>
              <Field label="Notes (optionnel)">
                <textarea
                  data-testid="match-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white h-20 resize-none"
                  placeholder="Consignes, format..."
                />
              </Field>
              <button
                data-testid="submit-create-match"
                type="submit"
                className="w-full py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg"
              >
                Programmer le match
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function MatchRow({ m, isCoach, onDelete, finished }) {
  const isOfficial = m.match_type === "official";
  const isGala = m.match_type === "gala";
  const typeLabel = isOfficial ? "Officiel" : isGala ? "Gala" : "Interne";
  const typeClass = isOfficial
    ? "bg-[#E5B85C]/20 text-[#E5B85C]"
    : isGala
    ? "bg-purple-500/20 text-purple-300"
    : "bg-slate-800 text-slate-300";
  return (
    <div
      data-testid={`match-row-${m.id}`}
      className="p-4 rounded-xl bg-slate-900 border border-white/10 hover:border-[#E5B85C]/40 transition-colors"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-widest font-mono-stat ${typeClass}`}
            >
              {typeLabel}
            </span>
            <div className="font-display font-bold text-lg truncate">
              {isOfficial
                ? `${m.title || "Match"} · vs ${m.opponent_name || "Adversaire"}`
                : isGala
                ? `${m.title || "Match de gala"} · ${m.team_a_name || "Équipe A"} vs ${m.team_b_name || "Équipe B"}`
                : m.title || "Match ASS les Régionnaires"}
            </div>
            {finished && !isOfficial && !isGala && (
              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-200 text-xs font-mono-stat">
                <span className="text-sky-400">Bleus {m.score_bleus}</span>
                <span className="text-slate-500 mx-1">-</span>
                <span className="text-rose-400">{m.score_rouges} Rouges</span>
              </span>
            )}
            {finished && isOfficial && (
              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-200 text-xs font-mono-stat">
                <span className="text-[#E5B85C]">Nous {m.our_score}</span>
                <span className="text-slate-500 mx-1">-</span>
                <span className="text-slate-300">{m.opponent_score} {m.opponent_name}</span>
              </span>
            )}
            {finished && isGala && (
              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-200 text-xs font-mono-stat">
                <span className="text-purple-300">{m.team_a_name} {m.score_team_a ?? 0}</span>
                <span className="text-slate-500 mx-1">-</span>
                <span className="text-fuchsia-300">{m.score_team_b ?? 0} {m.team_b_name}</span>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-slate-400 font-mono-stat">
            <span className="flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> {formatDate(m.match_date)}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {m.location}
            </span>
            {isOfficial && (
              <span className="uppercase text-slate-500">{m.home ? "Domicile" : "Extérieur"}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/matches/${m.id}`}
            data-testid={`open-match-${m.id}`}
            className="px-4 py-2 rounded-lg bg-[#E5B85C] text-[#0B0F17] font-bold text-xs uppercase tracking-wide"
          >
            Ouvrir
          </Link>
          {isCoach && (
            <button
              data-testid={`delete-match-${m.id}`}
              onClick={() => onDelete(m.id)}
              className="p-2 rounded-lg bg-slate-800 hover:bg-rose-500/20 text-slate-400 hover:text-rose-300"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
